import axios, { AxiosInstance } from 'axios'
import API_CONFIG from '../config/api.config'
import authRepository from './auth.repository'
import { CreateCommunityDTO, InviteMemberDTO, AssignOwnerDTO } from '../types'

export interface Plan {
  id: number
  name: string
  price: number
  features: string[]
}

export interface Permission {
  id: number
  name: string
  description: string
}

export interface ClubPackage {
  id: number
  name: string
  defaultThreshold: number
  swisetPlan: string
  plan: Plan | null
  permissions: Permission[] | null
}

export interface PartnerBrand {
  id: number
  fullLogo: string
  responsiveLogo: string
}

export interface Partner {
  id: number
  shortName: string
  emailDomain: string | null
  fullName: string
  mainColor: string
  responsiveLogoUrl: string
  fullLogoUrl: string
  websiteUrl: string | null
  instagramProfileUrl: string | null
  facebookProfileUrl: string | null
  twitterProfileUrl: string | null
  proExpirationDate: string
  partnerBrand: PartnerBrand
}

export interface Strategy {
  id: number
  name: string
  description: string
  type: string
  status: string
}

export interface EpaycoDetails {
  customerId: string
  planId: string
  subscriptionId: string
  status: string
}

export interface Batch {
  id: number
  name: string
  startDate: string
  endDate: string
  status: string
}

export interface Club {
  id: number
  name: string
  description: string | null
  createdAt: string
  strategies: Strategy[] | null
  imageUrl: string
  primaryColor: string
  proExpirationTime: string
  threshold: number
  paypalSubscriptionId: string | null
  epaycoDetails: EpaycoDetails | null
  clubPackage: ClubPackage
  permissions: Permission[] | null
  batches: Batch[] | null
  partner: Partner
  partnerOwned: boolean
}

/**
 * Simplified owned club interface for community selection
 * Used in tournament/competition creation wizard
 */
export interface OwnedClub {
  id: number
  name: string
  imageUrl: string
  threshold: number
}

class ClubsRepository {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      config => {
        const authHeaders = authRepository.getAuthHeader()
        if (authHeaders) {
          config.headers = { ...config.headers, ...authHeaders }
        }
        return config
      },
      error => Promise.reject(error)
    )

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          authRepository.clearAuthData()
          window.location.href = '/broker/login'
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Get all clubs associated with a partner
   * @param partnerId - The ID of the partner
   * @returns Promise with the clubs associated with the partner
   */
  async getPartnerClubs(partnerId: number): Promise<Club[]> {
    try {
      const response = await this.api.get<Club[]>(
        API_CONFIG.CLUBS.PARTNER_CLUBS(partnerId)
      )
      return response.data
    } catch (error) {
      console.error('Error fetching partner clubs:', error)
      throw error
    }
  }

  /**
   * Get the main community (club) for a partner
   * This is determined by the partnerOwned flag
   * @param partnerId - The ID of the partner
   * @returns Promise with the main club
   */
  async getPartnerMainCommunity(partnerId: number): Promise<Club | null> {
    try {
      const clubs = await this.getPartnerClubs(partnerId)
      const club = clubs.find(club => club.club.partnerOwned) || null
      console.log('Partner main community:', club);
      return club;
    } catch (error) {
      console.error('Error fetching partner main community:', error)
      throw error
    }
  }

  /**
   * Get all owned communities for a partner
   * These are communities where the partner is the owner (not invited/IB)
   * Used in tournament/competition creation to select which community to create for
   * @param partnerId - The ID of the partner
   * @returns Promise with array of owned clubs
   */
  async getOwnedClubs(partnerId: number): Promise<OwnedClub[]> {
    try {
      const response = await this.api.get<OwnedClub[]>(
        API_CONFIG.CLUBS.OWNED_CLUBS(partnerId)
      )
      console.log('Owned clubs:', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching owned clubs:', error)
      throw error
    }
  }

  /**
   * Create a new community/club
   * @param clubData - The community creation data
   * @returns Promise with the created club
   */
  async createClub(clubData: CreateCommunityDTO): Promise<Club> {
    try {
      console.log('Creating community:', clubData)

      // Validate required fields
      if (!clubData.userId) {
        throw new Error('User ID is required to create a community')
      }

      const formData = new FormData()

      // Required fields
      formData.append('name', clubData.name)
      formData.append('description', clubData.description)
      formData.append('threshold', '100')
      formData.append('userId', clubData.userId)
      formData.append('partnerId', String(clubData.partnerId))
      formData.append('typeUser', 'OWNER')
      formData.append('partnerOwned', String(clubData.partnerOwned)) // Dynamically set based on main club existence

      // Set expiration date to 6 months from now (max allowed by backend)
      // Subtract 1 day to ensure we're within the limit and avoid timezone edge cases
      const expirationDate = new Date()
      expirationDate.setMonth(expirationDate.getMonth() + 6)
      expirationDate.setDate(expirationDate.getDate() - 1)
      expirationDate.setHours(23, 59, 59, 0)
      formData.append('proExpirationDate', expirationDate.toISOString())

      // Add all colors (remove # if present)
      const cleanColor = (color: string) => color.replace('#', '')
      formData.append('primaryColor', cleanColor(clubData.primaryColorLight))
      formData.append('primaryColorLight', cleanColor(clubData.primaryColorLight))
      formData.append('secondaryColorLight', cleanColor(clubData.secondaryColorLight))
      formData.append('accentColorLight', cleanColor(clubData.accentColorLight))
      formData.append('primaryColorDark', cleanColor(clubData.primaryColorDark))
      formData.append('secondaryColorDark', cleanColor(clubData.secondaryColorDark))
      formData.append('accentColorDark', cleanColor(clubData.accentColorDark))

      // Add image file if provided
      if (clubData.imageFile) {
        formData.append('file', clubData.imageFile)
      }

      const response = await this.api.post(API_CONFIG.CLUBS.CREATE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      console.log('Community created successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('Error creating community:', error)
      throw error
    }
  }

  /**
   * Invite members to an existing club/community
   * @param inviteData - The invitation data (emails, clubId)
   * @returns Promise with the number of invites sent
   */
  async inviteMembers(inviteData: InviteMemberDTO): Promise<number> {
    try {
      console.log('Inviting members:', inviteData)

      const response = await this.api.post(
        API_CONFIG.CLUBS.INVITE_MEMBERS,
        inviteData,
        { headers: { 'Content-Type': 'application/json' } }
      )

      console.log('Invites sent successfully:', response.data)
      // API returns the number of invites sent
      return response.data || inviteData.emails.length
    } catch (error) {
      console.error('Error inviting members:', error)
      throw error
    }
  }

  /**
   * Get social invite code for a club
   * @param clubId - The club ID
   * @returns Promise with the invite code string
   */
  async getSocialInviteCode(clubId: number): Promise<string> {
    try {
      const response = await this.api.get<string>(
        API_CONFIG.CLUBS.GET_SOCIAL_INVITE_CODE(clubId)
      )
      // API returns the code as plain text response
      console.log('Social invite code retrieved:', response.data)
      return response.data
    } catch (error) {
      console.error('Failed to get social invite code:', error)
      throw new Error('Failed to generate invite code')
    }
  }

  /**
   * Send admin invitation to manage a community
   * @param assignData - The invitation data (emails, threshold, notifyPartner, inviterId, partnerId)
   * @returns Promise that resolves when invitation is sent (202 status)
   */
  async sendAdminInvitation(assignData: AssignOwnerDTO): Promise<void> {
    try {
      console.log('Sending admin invitation:', assignData)

      const response = await this.api.post(
        API_CONFIG.CLUBS.ADMIN_INVITATIONS,
        assignData,
        { headers: { 'Content-Type': 'application/json' } }
      )

      console.log('Admin invitation sent successfully, status:', response.status)
    } catch (error) {
      console.error('Error sending admin invitation:', error)
      throw error
    }
  }
}

export default new ClubsRepository()
